import { ChatMessage } from '@/types'
import { AttachedSectionChip } from './AttachedSectionChip'
import { AssistantMarkdownContent } from './AssistantMarkdownContent'
import { cn } from '@/lib/utils'
import { useState } from 'react'
import { User, Sparkles, Copy, Check } from 'lucide-react'

interface ChatMessageBubbleProps {
  message: ChatMessage
}

function formatMessageTimestamp(iso: string | undefined): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  const now = new Date()
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  if (sameDay) {
    return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
  }
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function ChatMessageBubble({ message }: ChatMessageBubbleProps) {
  const isUser = message.role === 'user'
  const [isCopied, setIsCopied] = useState(false)

  const text = message.parts.map((part) => part.text).join('')
  const timeLabel = formatMessageTimestamp(message.createdAt)

  async function handleCopy() {
    if (!text) return
    try {
      await navigator.clipboard.writeText(text)
      setIsCopied(true)
      window.setTimeout(() => setIsCopied(false), 1500)
    } catch (error) {
      console.error('Failed to copy message', error)
    }
  }

  return (
    <div
      className={cn(
        'group flex gap-2.5',
        isUser ? 'flex-row-reverse' : 'flex-row'
      )}
    >
      {/* Avatar */}
      <div
        className={cn(
          'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 border',
          isUser
            ? 'bg-[var(--avatar-user-bg)] border-[var(--avatar-user-border)] dark:bg-primary dark:border-transparent'
            : 'bg-[var(--avatar-assistant-bg)] border-[var(--avatar-assistant-border)] dark:bg-gradient-to-br dark:from-blue-500 dark:to-purple-600 dark:border-transparent'
        )}
      >
        {isUser ? (
          <User className="h-4 w-4 text-[var(--avatar-user-fg)] dark:text-white" />
        ) : (
          <Sparkles className="h-4 w-4 text-[var(--avatar-assistant-fg)] dark:text-white" />
        )}
      </div>

      {/* Message Bubble */}
      <div
        className={cn(
          'relative flex max-w-[84%] flex-col rounded-2xl px-4 py-3 text-sm',
          isUser
            ? 'bg-[var(--bubble-user-bg)] text-[var(--bubble-user-fg)] border border-[var(--bubble-user-border)] rounded-tr-sm dark:bg-primary dark:text-white dark:border-transparent'
            : 'bg-white text-[#101828] border border-[rgba(15,23,42,0.09)] shadow-[var(--shadow-card)] rounded-tl-sm dark:bg-white/10 dark:text-white dark:border-white/10 dark:shadow-none'
        )}
      >
        {isUser ? (
          <div className="space-y-2">
            {message.attachedSections && message.attachedSections.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {message.attachedSections.map((row) => (
                  <AttachedSectionChip key={`${row.section}-${row.pageNumber}`} row={row} compact />
                ))}
              </div>
            ) : null}
            <span className="whitespace-pre-wrap">{text}</span>
          </div>
        ) : (
          <AssistantMarkdownContent content={text} />
        )}

        <div
          className={cn(
            'mt-2 flex items-center justify-between gap-2 border-t pt-2',
            isUser ? 'border-[var(--bubble-user-border)] dark:border-white/20' : 'border-[rgba(15,23,42,0.09)] dark:border-white/10'
          )}
        >
          <span className="min-h-[14px] text-[11px] tabular-nums text-[#576274] dark:text-gray-400">
            {timeLabel ?? '\u00a0'}
          </span>
          <button
            type="button"
            onClick={handleCopy}
            disabled={!text}
            className={cn(
              'inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium transition-colors',
              isUser
                ? 'text-[#6d28d9]/80 hover:bg-[rgba(147,51,245,0.1)] hover:text-[#6d28d9] dark:text-white/80 dark:hover:bg-white/15 dark:hover:text-white'
                : 'text-[#576274] hover:bg-[var(--lm-hover)] hover:text-[#101828] dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-white',
              !text && 'pointer-events-none opacity-40'
            )}
            title={isCopied ? 'Copied' : 'Copy message'}
            aria-label={isCopied ? 'Copied' : 'Copy message'}
          >
            {isCopied ? (
              <>
                <Check className="h-3 w-3" />
                Copied
              </>
            ) : (
              <>
                <Copy className="h-3 w-3" />
                Copy
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
